import { axiosClient } from '@/shared';
import { useSpaceStore } from '@/shared/store/space-store';
import { ApiResponse } from '@/shared/types';
import { CustomQueryOptions } from '@/shared/types/global/react-query/query';
import { useQuery } from '@tanstack/react-query';
import { z, ZodError } from 'zod';

/**
 * space 단위 UI 프로파일 serve 응답 (GET /v1.0/space/ui-profile).
 *
 * 저장된 delta 원문을 그대로 받는다(상속/base 합성 없음). 미설정 필드는 null 이며
 * FE 가 base 로 폴백한다. uiConfig/sidebarLogo/featureConfig 는 카탈로그별 opaque JSON.
 */
const spaceUiMenuSchema = z.object({
  catalogItemId: z.string(),
  enabled: z.boolean(),
  sortOrder: z.number(),
  affiliationId: z.number().nullable().default(null),
  groupOrder: z.number().default(0),
  instanceId: z.string(),
  featureConfig: z.record(z.unknown()).nullable().default(null),
});

/**
 * canonical 사이드바 로고 serve shape (P11 S1). 홈·playground 동일.
 * variants.light 필수, dark optional. 저장 shape 가 이미 해석된 url 이라 env-keying 없음.
 * 미설정이면 컬럼 NULL → sidebarLogo: null.
 */
const sidebarLogoVariantSchema = z.object({
  url: z.string(),
  contentId: z.number().optional(),
});

const sidebarLogoSchema = z.object({
  alt: z.string().optional(),
  caption: z.string().optional(),
  variants: z.object({
    light: sidebarLogoVariantSchema,
    dark: sidebarLogoVariantSchema.optional(),
  }),
});

/**
 * space 단위 추천/게이트 리소스. 저장된 정책 delta 를 그대로 노출한다.
 * null(정책 미설정)이면 FE 는 base 폴백(폰트 게이트·추천 없음).
 * - allowedFontIds: 커스텀 폰트 가시성 게이트(허용 font family id 목록).
 * - recommendedFontIds: 폰트 picker 추천-우선 노출용 font family id 목록.
 * - recommendedAvatarIds: 아바타 목록 추천-우선 노출용 아바타 id 목록.
 */
const spaceUiResourcesSchema = z.object({
  allowedFontIds: z.array(z.string()).default([]),
  recommendedFontIds: z.array(z.string()).default([]),
  recommendedAvatarIds: z.array(z.number()).default([]),
});

/**
 * LTS 부가기능 유효 정책(프로필 기본값 + space override 를 BE 가 합성 완료한 값).
 * landingDestination 과 무관하게 서빙된다. 구버전 서버는 필드가 없어 null —
 * FE 는 기존 homeMenus lts.highlight featureConfig 경로로 폴백한다.
 */
const ltsFeatureConfigSchema = z.object({
  supportedModes: z.array(z.string()).default([]),
  defaultModeId: z.string().nullable().default(null),
  lockedModeId: z.string().nullable().default(null),
  showModeStep: z.boolean(),
  showAnalysisFilters: z.boolean(),
  forceAdvancedOnStaticFilter: z.boolean(),
  // null = 커머스 정책 미명시(프로필·space 어디에도 키 없음) → 기존 게이트(showAnalysisFilters) 동등 폴백.
  // 구버전 GS space(시드 전)의 재필터·파이프라인이 배포만으로 꺼지지 않게 하는 3-state.
  commerceEnabled: z.boolean().nullable().default(null),
  buttonI18nKey: z.string().nullable().default(null),
  defaultIndexLevel: z.number().nullable().default(null),
  uploadIndexLevel: z.number().nullable().default(null),
  // LTS 자막 번역(생성 옵션·에디터 번역) 노출·실행 게이트. 신규 기능이라 폴백 대상이 없어 2-state 이고
  // 키 부재(구버전 서버)는 코드 기본값 false 로 수렴한다.
  captionTranslationEnabled: z.boolean().default(false),
});

export const spaceUiProfileSchema = z.object({
  uiConfig: z.record(z.unknown()).nullable().default(null),
  landingDestination: z.enum(['HOME', 'PLAYGROUND']).nullable().default(null),
  // 기능 프로필 기반 신규 게이트. 구버전/비정상 응답은 기존 내보내기 동작(false)으로 닫는다.
  exportFpsSelectionEnabled: z.boolean().catch(false).default(false),
  resources: spaceUiResourcesSchema.nullable().catch(null).default(null),
  // malformed 여도 이 필드만 null 로 degrade(→ 기존 homeMenus 경로 폴백), 전체 파싱은 유지.
  ltsFeatureConfig: ltsFeatureConfigSchema.nullable().catch(null).default(null),
  // 전환기 방어: 비-canonical/legacy 로고 shape 가 오더라도 로고만 null 로 degrade 하고
  // 프로필 전체 파싱은 깨지지 않게 한다(정적 tenant 폴백으로 수렴). BE 는 이미 non-canonical 을
  // null 로 드롭하지만, 이중 안전장치로 FE 도 malformed 를 흡수한다.
  sidebarLogo: sidebarLogoSchema.nullable().catch(null).default(null),
  homeMenus: z.array(spaceUiMenuSchema).default([]),
  playgroundMenus: z.array(spaceUiMenuSchema).default([]),
});

export type SpaceUiMenuResType = z.infer<typeof spaceUiMenuSchema>;
export type SpaceUiResourcesResType = z.infer<typeof spaceUiResourcesSchema>;
export type SpaceUiProfileResType = z.infer<typeof spaceUiProfileSchema>;
export type SpaceUiSidebarLogoResType = z.infer<typeof sidebarLogoSchema>;
export type SpaceUiLtsFeatureConfigResType = z.infer<
  typeof ltsFeatureConfigSchema
>;

type SpaceUiProfileResponse = ApiResponse<SpaceUiProfileResType>;

export const spaceUiProfileApi = async (): Promise<SpaceUiProfileResponse> => {
  try {
    const response = await axiosClient.get<SpaceUiProfileResponse>(
      '/v1.0/space/ui-profile',
    );
    const parsed = spaceUiProfileSchema.parse(response.data.data);
    return { ...response.data, data: parsed };
  } catch (error) {
    if (error instanceof ZodError) {
      console.error('space ui-profile 응답 검증 실패:', error.issues);
      return Promise.reject(
        new Error('서버 응답 데이터 형식이 올바르지 않습니다.'),
      );
    }
    return Promise.reject(error);
  }
};

export const QueryKeyUseSpaceUiProfile = 'spaceUiProfile' as const;

export const useGetSpaceUiProfileApi = (
  options?: CustomQueryOptions<SpaceUiProfileResponse>,
) => {
  const { queryKey, enabled, ...rest } = options ?? {};
  const currentSpaceId = useSpaceStore((state) => state.spaceId);
  return useQuery({
    queryKey: [QueryKeyUseSpaceUiProfile, currentSpaceId, ...(queryKey ?? [])],
    queryFn: async () => await spaceUiProfileApi(),
    ...rest,
    enabled: !!currentSpaceId && (enabled ?? true),
  });
};
